import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { Background, ConnectionLineType, MarkerType, MiniMap, ReactFlow, useEdgesState, useNodesState } from 'reactflow'

import type { ChangeEvent, ReactElement } from 'react'
import type { ITask, ITaskExcecution } from '~/types/app-setup'
import type { IAppManagement } from '~/types/application-management'

import Banner from '~/components/Banner'
import Button from '~/components/Buttons/Button'
import BasicCheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import MwMLoader from '~/components/MwMLoader'
import {
  keys as appSetupKeys,
  useAppSetupQuery,
  useAppSetupTaksQuery,
  useCreateAppSetupMutation,
  useExecuteTaskMutation,
} from '~/queries/application-setup'
import { getLayoutedElements, nodeTypes } from '~/utils/react-flow'
import { displayToast } from '~/utils/toast'

import './styles.scss'
import 'reactflow/dist/style.css'

const platformOptions = [
  { label: 'iOS', value: 'ios' },
  { label: 'Android', value: 'android' },
  { label: 'HMS', value: 'hms' },
  { label: 'Web', value: 'web' },
]

const getEdgeStrokeColor = (status: ITaskExcecution['status']): string => {
  switch (status) {
    case 'completed':
      return '#06703d'
    case 'failed':
      return '#ca3423'
    case 'running':
      return '#00579a'
    case 'skipped':
      return '#626262'
    case 'pending':
    default:
      return 'undefined'
  }
}

const TasksExecutionFlow = ({ application }: { application: IAppManagement }): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [selectedPlatform, setSelectedPlatform] = useState<IOption>(platformOptions[0])

  const [nodes, setNodes] = useNodesState<ITask>([])

  const [edges, setEdges] = useEdgesState<ITask>([])

  const [isPolling, setIsPolling] = useState(true)

  const [isLoading, setIsLoading] = useState(false)

  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])

  /** Queries & Mutations */

  const { mutateAsync: createAppSetupMutation } = useCreateAppSetupMutation()

  const { mutateAsync: executeTaskMutation } = useExecuteTaskMutation()

  const { data } = useAppSetupQuery({
    appKey: application.app_key,
    deviceType: selectedPlatform.value,
    pollingEnabled: isPolling,
  })

  const { data: tasks, isLoading: isTasksLoading } = useAppSetupTaksQuery()

  /** Effects */

  const handleExecuteTask = useCallback(
    async (task: ITask) => {
      try {
        setIsPolling(true)
        await executeTaskMutation({
          appKey: application.app_key,
          deviceType: selectedPlatform.value,
          taskId: task.id,
        })
      } catch (error) {
        if (isAxiosError(error)) {
          const { kind } = error.response?.data || undefined
          switch (kind) {
            case 'missing_hms_bundle_id':
              displayToast('Please provide an valid HMS bundle ID', 'error')
              break
            default:
              captureException(kind)
              displayToast('An error occurred while executing the task', 'error')
          }
        } else {
          captureException(error)
          displayToast('An error occurred while executing the task', 'error')
        }
      } finally {
        await queryClient.invalidateQueries({
          queryKey: appSetupKeys.get({ appKey: application.app_key, deviceType: selectedPlatform.value }),
        })
      }
    },
    [application.app_key, executeTaskMutation, queryClient, selectedPlatform.value]
  )

  useEffect(() => {
    if (isPolling) {
      const shouldContinuePolling = data
        ? Object.values(data).some((task) => task.status !== 'failed' && task.status !== 'completed')
        : false
      setIsPolling(shouldContinuePolling)
    }
  }, [data, isPolling])

  useEffect(() => {
    if (data) {
      const initialNodes = []
      const initialEdges = []
      const tasks = Object.values(data)

      for (const task of tasks) {
        initialNodes.push({
          data: {
            ...task.setup_task,
            isRunnable: true,
            onExecute: handleExecuteTask,
            status: task.status,
          },
          id: task.setup_task.id,
          position: { x: 0, y: 0 },
          type: 'app-setup-card',
        })

        if (task.setup_task.previous_task_ids && task.setup_task.previous_task_ids.length) {
          for (const previousStepId of task.setup_task.previous_task_ids) {
            initialEdges.push({
              animated: task.status !== 'completed' && task.status !== 'skipped' && task.status !== 'failed',
              id: `${previousStepId}-${task.setup_task.id}`,
              markerEnd: {
                type: MarkerType.ArrowClosed,
              },
              source: previousStepId,
              style: {
                stroke: getEdgeStrokeColor(task.status),
              },
              target: task.setup_task.id,
            })
          }
        }
      }

      const { edges: layoutedEdges, nodes: layoutedNodes } = getLayoutedElements(initialNodes, initialEdges)

      setNodes(layoutedNodes)
      setEdges(layoutedEdges)
    }
  }, [data, setNodes, setEdges, handleExecuteTask])

  /** Handlers */

  const handleSelectDevice = (value: IOption) => {
    setSelectedPlatform(value)
    setIsPolling(true)
  }

  const handleTaskSelectChange = (taskId: string, evt: ChangeEvent<HTMLInputElement>) => {
    const isChecked = evt.target.checked
    if (isChecked) {
      setSelectedTaskIds((prevSelectedTaskIds) => [...prevSelectedTaskIds, taskId])
    } else {
      setSelectedTaskIds((prevSelectedTaskIds) => prevSelectedTaskIds.filter((id) => id !== taskId))
    }
  }

  const handleStartTask = async () => {
    setIsLoading(true)
    try {
      await createAppSetupMutation({
        appKey: application.app_key,
        deviceType: selectedPlatform.value,
        taskIds: selectedTaskIds,
      })
    } catch (error) {
      if (isAxiosError(error)) {
        const kind = error.response?.data?.kind
        switch (kind) {
          case 'missing_android_bundle_id':
          case 'missing_hms_bundle_id':
          case 'missing_ios_bundle_id':
          case 'missing_web_bundle_id':
            displayToast('Please provide an valid bundle ID', 'error')
            break
          default:
            console.error(error)
        }
      }
    } finally {
      setIsPolling(true)
      setIsLoading(false)
      await queryClient.invalidateQueries({
        queryKey: appSetupKeys.get({ appKey: application.app_key, deviceType: selectedPlatform.value }),
      })
    }
  }

  /** Render */

  return (
    <div className="tasks-execution-flow-container">
      <SelectInput
        label="Select a platform"
        onChange={(value) => handleSelectDevice(value as unknown as IOption)}
        options={platformOptions}
        value={selectedPlatform}
      />
      {data ? (
        <div className="tasks-execution-flow">
          <ReactFlow
            connectionLineType={ConnectionLineType.SmoothStep}
            edges={edges}
            fitView
            maxZoom={1}
            nodeTypes={nodeTypes}
            nodes={nodes}
            preventScrolling={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <MiniMap nodeStrokeWidth={3} />
          </ReactFlow>
        </div>
      ) : (
        <div>
          <Banner className="no-task-warning" variant="warning">
            No tasks found for this device
          </Banner>
          {isTasksLoading ? (
            <MwMLoader category="item" />
          ) : (
            <div className="tasks-list">
              {tasks
                ?.sort(({ id: idA }, { id: idB }) => (idA > idB ? 1 : -1))
                .map(({ description, id }) => (
                  <BasicCheckboxInput
                    className="task-select-input"
                    key={id}
                    label={`${id} - ${description}`}
                    labelPosition="right"
                    onChange={(value) => handleTaskSelectChange(id, value)}
                    value={id}
                  />
                ))}
              <Button disabled={isLoading || isPolling} onClick={handleStartTask}>
                {isLoading
                  ? 'Loading...'
                  : isPolling
                    ? 'Polling in progress'
                    : 'Execute selected tasks for this device'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TasksExecutionFlow
